<template>
  <div>
    <div class="models-container">
      <div
        :class="[...manualClass, disableModelSelection && selectedModel !== 'manual' ? 'segmented-dim segmented-disabled-cursor' : '']"
        @click="!disableModelSelection && (selectedModel = 'manual')"
      >
        <div class="icon-text-row">
          <mf-icon>person_play</mf-icon>
          <span class="icon-label">Distribuição Manual</span>
        </div>
        <span class="description">
          Todos os clientes do segmento recebem todas as ofertas configuradas nesta campanha. Ex: se a campanha tem 50 produtos, cada cliente receberá todos
          esses 50 produtos.
        </span>
      </div>

      <div
        :class="[...intelligentClass, disableModelSelection && selectedModel !== 'intelligent' ? 'segmented-dim segmented-disabled-cursor' : '']"
        @click="!disableModelSelection && (selectedModel = 'intelligent')"
      >
        <div class="icon-text-row">
          <img :src="require('@/assets/icons/mar.ia-cor.svg')" alt="maria" />
          <span class="icon-label">Distribuição inteligente</span>
        </div>
        <span class="description">
          Cada cliente do segmento recebe apenas uma seleção específica de ofertas, escolhidas automaticamente pela inteligência artificial com base no seu
          perfil de compra. Ex: se a campanha tem 50 produtos, cada cliente recebe apenas 10 ofertas, de acordo com o perfil.
        </span>
      </div>
    </div>

    <template v-if="selectedModel === 'intelligent'">
      <div class="intelligent-config-container">
        <div class="config-section">
          <span class="field-label">Critérios de seleção de oferta</span>

          <v-row class="criteria-container">
            <v-col v-for="criterion in criteria" :key="criterion.key" cols="12" md="6" lg="6">
              <div class="criterion-card">
                <div class="criterion-header">
                  <v-checkbox v-model="criteriaData[criterion.key].enabled" color="#0079F2" :disabled="disableModelSelection" class="criterion-checkbox" />
                  <h2 class="criterion-title">{{ criterion.title }}</h2>
                </div>
                <span class="criterion-description">{{ criterion.description }}</span>
                <div class="criterion-content">
                  <mf-number-input
                    v-model="criteriaData[criterion.key].quantity"
                    :min="1"
                    :max="50"
                    show-arrows
                    auto-format
                    dense
                    outlined
                    placeholder="1"
                    label="Quantidade"
                    class="number-input"
                    :disabled="disableModelSelection || !criteriaData[criterion.key].enabled"
                    :error="!!criterion.errors"
                    :error-messages="criterion.errors"
                  />
                  <span class="field-hint">Limite: 1-50 ofertas</span>
                </div>
              </div>
            </v-col>
          </v-row>

          <mf-info-card class="mt-3" variant="info" :v-html="totalOffersMessage"> </mf-info-card>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { DISTRIBUTION_MODEL_LABELS, DISTRIBUTION_MODEL_DESCRIPTIONS } from '@/modules/campanhas/gestao-ofertas-v2/helpers/distributionModels.js'

export default {
  name: 'CampaignModelToggle',
  props: {
    campaign: {
      type: Object,
      required: true,
    },
    validations: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedModel: this.campaign.auto_distributed ? 'intelligent' : 'manual',
      criteriaData: {
        repurchase: {
          enabled: false,
          quantity: 1,
        },
        activation: {
          enabled: false,
          quantity: 1,
        },
        recovery: {
          enabled: false,
          quantity: 1,
        },
      },
    }
  },
  computed: {
    disableModelSelection() {
      return ['finished', 'active', 'processing', 'scheduled'].includes(this.campaign.status)
    },
    manualClass() {
      return ['segmented-base', this.selectedModel === 'manual' ? 'segmented-active' : 'segmented-inactive']
    },
    intelligentClass() {
      return ['nonsegmented-base', this.selectedModel === 'intelligent' ? 'nonsegmented-active' : 'nonsegmented-inactive']
    },
    criteria() {
      return [
        {
          key: 'repurchase',
          model: 'REPURCHASE',
          title: DISTRIBUTION_MODEL_LABELS.REPURCHASE,
          description: DISTRIBUTION_MODEL_DESCRIPTIONS.REPURCHASE,
          enabled: this.criteriaData.repurchase.enabled,
          quantity: this.criteriaData.repurchase.quantity,
          errors: this.repurchaseErrors,
        },
        {
          key: 'activation',
          model: 'ACTIVATION',
          title: DISTRIBUTION_MODEL_LABELS.ACTIVATION,
          description: DISTRIBUTION_MODEL_DESCRIPTIONS.ACTIVATION,
          enabled: this.criteriaData.activation.enabled,
          quantity: this.criteriaData.activation.quantity,
          errors: this.activationErrors,
        },
      ]
    },
    totalOffers() {
      let total = 0
      if (this.criteriaData.repurchase.enabled && this.criteriaData.repurchase.quantity > 0) total += this.criteriaData.repurchase.quantity
      if (this.criteriaData.activation.enabled && this.criteriaData.activation.quantity > 0) total += this.criteriaData.activation.quantity
      return total
    },
    repurchaseEnabled() {
      return this.criteriaData.repurchase.enabled
    },
    repurchaseQuantity() {
      return this.criteriaData.repurchase.quantity
    },
    activationEnabled() {
      return this.criteriaData.activation.enabled
    },
    activationQuantity() {
      return this.criteriaData.activation.quantity
    },
    recoveryEnabled() {
      return this.criteriaData.recovery.enabled
    },
    recoveryQuantity() {
      return this.criteriaData.recovery.quantity
    },
    repurchaseErrors() {
      const val = this.validations?.campaign?.user_distribution_models?.repurchase
      if (!val || !val.$dirty || !val.$error) return ''
      const errors = []
      if (!val.required) errors.push('Informe a quantidade de ofertas.')
      if (!val.minValue) errors.push('A quantidade deve ser no mínimo 1.')
      if (!val.maxValue) errors.push('A quantidade deve ser no máximo 50.')
      return errors[0] || ''
    },
    activationErrors() {
      const val = this.validations?.campaign?.user_distribution_models?.activation
      if (!val || !val.$dirty || !val.$error) return ''
      const errors = []
      if (!val.required) errors.push('Informe a quantidade de ofertas.')
      if (!val.minValue) errors.push('A quantidade deve ser no mínimo 1.')
      if (!val.maxValue) errors.push('A quantidade deve ser no máximo 50.')
      return errors[0] || ''
    },
    recoveryErrors() {
      const val = this.validations?.campaign?.user_distribution_models?.recovery
      if (!val || !val.$dirty || !val.$error) return ''
      const errors = []
      if (!val.required) errors.push('Informe a quantidade de ofertas.')
      if (!val.minValue) errors.push('A quantidade deve ser no mínimo 1.')
      if (!val.maxValue) errors.push('A quantidade deve ser no máximo 50.')
      return errors[0] || ''
    },
    totalOffersMessage() {
      return `<span style="color: #004D99">Cada cliente do segmento receberá ${this.totalOffers} oferta${
        this.totalOffers > 1 || this.totalOffers === 0 ? 's' : ''
      } no total, com base em seu perfil e aos critérios de seleção informados.</span>`
    },
  },
  watch: {
    selectedModel(newModel, oldModel) {
      this.campaign.auto_distributed = newModel === 'intelligent'
      this.cleanupFieldsBasedOnModel(newModel)

      if (this.validations && this.validations.campaign && this.validations.campaign.auto_distributed && this.validations.campaign.auto_distributed.$touch) {
        this.validations.campaign.auto_distributed.$touch()
      }
    },
    'criteriaData.repurchase.enabled'(newValue) {
      if (newValue) {
        if (this.criteriaData.repurchase.quantity === 0) {
          this.criteriaData.repurchase.quantity = 1
        }
      } else {
        this.criteriaData.repurchase.quantity = 0
      }
      this.updateDistributionModels()
    },
    'criteriaData.repurchase.quantity'() {
      this.updateDistributionModels()
    },
    'criteriaData.activation.enabled'(newValue) {
      if (newValue) {
        if (this.criteriaData.activation.quantity === 0) {
          this.criteriaData.activation.quantity = 1
        }
      } else {
        this.criteriaData.activation.quantity = 0
      }
      this.updateDistributionModels()
    },
    'criteriaData.activation.quantity'() {
      this.updateDistributionModels()
    },
    'criteriaData.recovery.enabled'(newValue) {
      if (newValue) {
        if (this.criteriaData.recovery.quantity === 0) {
          this.criteriaData.recovery.quantity = 1
        }
      } else {
        this.criteriaData.recovery.quantity = 0
      }
      this.updateDistributionModels()
    },
    'criteriaData.recovery.quantity'() {
      this.updateDistributionModels()
    },
  },
  mounted() {
    this.initializeFromCampaign()
    this.cleanupFieldsBasedOnModel(this.selectedModel)
    this.$emit('register-validation', this.validateIntelligentConfig)
  },
  methods: {
    initializeFromCampaign() {
      this.criteriaData.repurchase.enabled = false
      this.criteriaData.repurchase.quantity = 0
      this.criteriaData.activation.enabled = false
      this.criteriaData.activation.quantity = 0
      this.criteriaData.recovery.enabled = false
      this.criteriaData.recovery.quantity = 0

      if (this.campaign.user_distribution_models && this.campaign.user_distribution_models.length > 0) {
        this.campaign.user_distribution_models.forEach((model) => {
          if (model.model === 'REPURCHASE') {
            this.criteriaData.repurchase.enabled = true
            this.criteriaData.repurchase.quantity = model.quantity || 1
          } else if (model.model === 'ACTIVATION') {
            this.criteriaData.activation.enabled = true
            this.criteriaData.activation.quantity = model.quantity || 1
          }
        })
      }
    },
    updateDistributionModels() {
      if (!this.campaign.user_distribution_models) {
        this.$set(this.campaign, 'user_distribution_models', [])
      }

      const models = []

      if (this.criteriaData.repurchase.enabled && this.criteriaData.repurchase.quantity > 0) {
        models.push({
          model: 'REPURCHASE',
          quantity: this.criteriaData.repurchase.quantity,
        })
      }

      if (this.criteriaData.activation.enabled && this.criteriaData.activation.quantity > 0) {
        models.push({
          model: 'ACTIVATION',
          quantity: this.criteriaData.activation.quantity,
        })
      }

      this.campaign.user_distribution_models = models

      if (
        this.validations &&
        this.validations.campaign &&
        this.validations.campaign.user_distribution_models &&
        this.validations.campaign.user_distribution_models.$touch
      ) {
        this.validations.campaign.user_distribution_models.$touch()
      }
    },
    cleanupFieldsBasedOnModel(model) {
      if (model === 'manual') {
        if (this.campaign.hasOwnProperty('user_distribution_models')) {
          this.$delete(this.campaign, 'user_distribution_models')
        }
      } else if (model === 'intelligent') {
        if (!this.campaign.hasOwnProperty('user_distribution_models')) {
          this.updateDistributionModels()
        }
      }
    },
    validateIntelligentConfig() {
      if (this.selectedModel === 'intelligent') {
        if (!this.criteriaData.repurchase.enabled && !this.criteriaData.activation.enabled) {
          return false
        }

        if (this.criteriaData.repurchase.enabled) {
          if (!this.criteriaData.repurchase.quantity || this.criteriaData.repurchase.quantity < 1 || this.criteriaData.repurchase.quantity > 50) {
            return false
          }
        }

        if (this.criteriaData.activation.enabled) {
          if (!this.criteriaData.activation.quantity || this.criteriaData.activation.quantity < 1 || this.criteriaData.activation.quantity > 50) {
            return false
          }
        }
      }
      return true
    },
  },
}
</script>

<style lang="scss" scoped>
.card-container {
  margin-top: 24px;
}

.title {
  display: block;
  font-size: 20px;
  font-weight: 700;
  color: #4b525a;
  margin-bottom: 20px;
}

.models-container {
  display: flex;
  justify-content: space-between;
}

.description {
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: #4b525a;
  margin-top: 4px;
}

.intelligent-config-container {
  margin-top: 20px;
}

.config-section {
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
}

.field-label {
  display: block;
  font-size: 18px;
  font-weight: 700;
  color: $cinza-d10;
  margin-bottom: 12px;
}

.criteria-container {
  margin-bottom: 24px;
}

.criterion-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
}

.criterion-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.criterion-checkbox {
  width: 24px;
  height: 24px;
  margin: 0;
  padding: 0;
}

.criterion-title {
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.01em;
  color: #4b525a;
}

.criterion-description {
  width: 100%;
  height: auto;
  min-height: 20px;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #4b525a;
  margin: 0;
}

.criterion-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
}

.number-input {
  width: 100%;
  margin-top: 4px;
}

.field-hint {
  display: block;
  font-size: 10px;
  font-weight: 400;
  color: #31363b;
  margin-top: -20px;
}

.icon-text-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 0px;
}

.icon-label {
  display: block;
  font-size: 18px;
  font-weight: 400;
  color: #4b525a;
  margin-left: 8px;
}

.icon-text-row .v-icon {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.segmented-base {
  flex: 1;
  margin-top: 12px;
  padding: 12px 20px;
  border-style: solid;
  border-radius: 14px 0 0 14px;
  cursor: pointer;
}

.segmented-active {
  border-width: 2px !important;
  border-color: #0063c6 !important;
  background-color: #d2e9ff33 !important;
}

.segmented-inactive {
  border-width: 1px !important;
  border-color: #6b7580 !important;
  background-color: #fff !important;
}

.segmented-disabled-cursor {
  cursor: default !important;
}

.segmented-dim {
  opacity: 0.5 !important;
}

.nonsegmented-base {
  flex: 1;
  margin-top: 12px;
  padding: 12px 20px;
  border-style: solid;
  border-radius: 0 14px 14px 0;
  cursor: pointer;
}

.nonsegmented-active {
  border-width: 2px !important;
  border-color: #0063c6 !important;
  background-color: #d2e9ff33 !important;
}

.nonsegmented-inactive {
  border-width: 1px !important;
  border-color: #6b7580 !important;
  background-color: #fff !important;
}

.nonsegmented-disabled-cursor {
  cursor: default !important;
}

.nonsegmented-dim {
  opacity: 0.5 !important;
}

@media (max-width: 960px) {
  .models-container {
    flex-direction: column;
    align-items: flex-start;
  }

  .segmented-base,
  .nonsegmented-base {
    width: 100%;
    border-radius: 14px;
    margin-top: 8px;
  }
}
</style>
